import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import { PeakDetail } from "../types";

interface MicroTimingVisualizerProps {
  peaks: PeakDetail[];
  deviations: number[];
  toleranceMs: number;
  duration: number;
  levelId?: number;
}

const safeNum = (v: unknown, fallback: number) => {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
};

export const MicroTimingVisualizer: React.FC<MicroTimingVisualizerProps> = ({
  peaks = [],
  deviations = [],
  toleranceMs = 50,
  duration = 30,
  levelId = 4,
}) => {
  const { scatterData, distributionData, stats } = useMemo(() => {
    const devArray = Array.isArray(deviations) ? deviations : [];
    const peakArray = Array.isArray(peaks) ? peaks : [];

    if (devArray.length === 0) {
      return { scatterData: [], distributionData: [], stats: null as any };
    }

    const count = Math.min(devArray.length, peakArray.length || devArray.length);
    const dur = safeNum(duration, 30);

    const sData: Array<{
      time: number;
      offset: number;
      status: string;
      color: string;
      intensity: number;
    }> = [];

    const binSize = 10;
    const bins: Record<number, number> = {};
    for (let b = -20; b <= 20; b++) bins[b * binSize] = 0;

    let sum = 0;
    let inPocketCount = 0;

    for (let i = 0; i < count; i++) {
      const offset = Number(devArray[i]);
      if (!Number.isFinite(offset)) continue;

      const peak = peakArray[i] as any;
      const time = safeNum(peak?.time, i * (dur / Math.max(1, count)));
      const intensity = safeNum(peak?.intensity, 0.5);

      const isSafe = Math.abs(offset) <= toleranceMs;
      if (isSafe) inPocketCount++;

      sData.push({
        time,
        offset,
        status: isSafe ? "In Pocket" : offset < 0 ? "Rushing" : "Dragging",
        color: isSafe ? "#34d399" : offset < 0 ? "#ff6261" : "#60a5fa",
        intensity,
      });

      const b = Math.round(offset / binSize) * binSize;
      if (b >= -200 && b <= 200) bins[b] = (bins[b] || 0) + 1;

      sum += offset;
    }

    const distData = Object.entries(bins)
      .map(([ms, val]) => {
        const m = parseInt(ms, 10);
        return {
          ms: m,
          count: val,
          color:
            Math.abs(m) <= toleranceMs
              ? "#34d399"
              : m < 0
                ? "#ff6261"
                : "#60a5fa",
        };
      })
      .sort((a, b) => a.ms - b.ms);

    const mean = sum / Math.max(1, sData.length);
    const safeMean = Number.isFinite(mean) ? mean : 0;

    let signature = "The Pocket";
    if (safeMean < -15) signature = "Tendance : Rushing (Stress)";
    else if (safeMean > 15) signature = "Tendance : Dragging (Laid-back)";

    return {
      scatterData: sData,
      distributionData: distData,
      stats: {
        mean: safeMean,
        signature,
        pocketPct: Math.round((inPocketCount / Math.max(1, sData.length)) * 100),
        duration: dur,
      },
    };
  }, [peaks, deviations, toleranceMs, duration]);

  if (!stats || scatterData.length === 0) {
    return (
      <div className="bg-[#050b0d] p-12 rounded-[56px] border border-white/5 flex flex-col items-center justify-center min-h-[400px] opacity-40 text-center gap-6 animate-pulse">
        <div className="w-20 h-20 rounded-full border-2 border-slate-700 flex items-center justify-center text-4xl">
          🧬
        </div>
        <div className="space-y-2">
          <h3 className="text-xl font-black text-white uppercase italic">
            Analyse Finesse Indisponible
          </h3>
          <p className="text-[9px] text-slate-500 uppercase font-bold max-w-xs leading-relaxed">
            Le moteur n&apos;a pas pu isoler assez d&apos;attaques nettes pour corréler le placement micro-temporel.
            Jouez plus franchement !
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#01080a] p-8 md:p-12 rounded-[64px] border border-white/5 shadow-2xl flex flex-col h-fit md:h-[750px] relative overflow-hidden group">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 shrink-0 z-20 gap-6">
        <div className="space-y-1">
          <h3 className="text-3xl font-black text-white italic uppercase tracking-tighter flex items-center gap-3">
            Pocket <span className="text-purple-400">Positioning</span>
          </h3>
          <p className="text-[10px] text-slate-500 uppercase font-bold tracking-[0.4em] ml-1">
            Analyse du placement micro-temporel (Feel)
          </p>
        </div>

        <div className="flex gap-4">
          <div className="bg-white/5 px-6 py-3 rounded-3xl border border-white/10 flex flex-col items-center">
            <span className="text-[8px] font-black text-slate-500 uppercase">Pocket Accuracy</span>
            <span className="text-xl font-black text-emerald-400 italic">{stats.pocketPct}%</span>
          </div>
          <div className="bg-white/5 px-6 py-3 rounded-3xl border border-white/10 flex flex-col items-center">
            <span className="text-[8px] font-black text-slate-500 uppercase">Biais (Feel)</span>
            <span
              className={`text-xl font-black italic ${
                Math.abs(stats.mean) < 10
                  ? "text-white"
                  : stats.mean < 0
                    ? "text-red-400"
                    : "text-blue-400"
              }`}
            >
              {Math.round(stats.mean)}ms
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-[400px]">
        <div className="lg:col-span-8 bg-black/40 rounded-[40px] border border-white/5 relative overflow-hidden p-4">
          <ResponsiveContainer width="100%" height="100%" debounce={50}>
            <ScatterChart margin={{ top: 40, bottom: 20, left: 0, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" vertical={false} />
              <XAxis type="number" dataKey="time" hide domain={[0, stats.duration]} />
              <YAxis type="number" dataKey="offset" hide domain={[-200, 200]} />
              <ZAxis type="number" dataKey="intensity" range={[50, 200]} />
              <ReferenceArea y1={-toleranceMs} y2={toleranceMs} fill="#10b981" fillOpacity={0.08} />
              <ReferenceLine y={0} stroke="#fff" strokeOpacity={0.2} strokeDasharray="5 5" />
              <Scatter data={scatterData}>
                {scatterData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={entry.color}
                    style={{ filter: `drop-shadow(0 0 8px ${entry.color}66)` }}
                  />
                ))}
              </Scatter>
              <Tooltip
                cursor={{ strokeDasharray: "3 3" }}
                content={({ active, payload }) => {
                  if (active && payload?.[0]) {
                    const d: any = payload[0].payload;
                    return (
                      <div className="bg-black/90 border border-white/20 p-3 rounded-2xl backdrop-blur-md shadow-2xl">
                        <p className="text-sm font-black italic" style={{ color: d.color }}>
                          {Math.round(d.offset)}ms ({d.status})
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-4 bg-black/40 rounded-[40px] border border-white/5 flex flex-col p-6 shadow-inner">
          <div className="mb-6">
            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mb-1">
              Density Histogram
            </h4>
            <p className="text-[9px] text-slate-600 italic">Distribution des attaques</p>
          </div>

          <div className="flex-1">
            <ResponsiveContainer width="100%" height="100%" debounce={50}>
              <BarChart data={distributionData} layout="vertical" margin={{ left: -30 }}>
                <XAxis type="number" hide />
                <YAxis dataKey="ms" type="category" hide />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {distributionData.map((entry, index) => (
                    <Cell
                      key={`bin-${index}`}
                      fill={entry.color}
                      fillOpacity={(entry as any).count > 0 ? 0.8 : 0.05}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-6 p-5 bg-white/5 rounded-[32px] border border-white/5">
            <div className="text-[9px] font-black text-smart-gold uppercase tracking-widest mb-2 flex items-center gap-2">
              Feel Analysis
            </div>
            <div className="text-sm font-bold text-white italic leading-tight">{stats.signature}</div>
          </div>
        </div>
      </div>

      <div className="mt-8 flex items-center justify-between bg-black/40 p-6 rounded-[32px] border border-white/5">
        <div className="flex items-center gap-6">
          <div className="w-12 h-12 rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-xl">
            💡
          </div>
          <p className="text-[11px] text-slate-400 font-medium italic leading-relaxed max-w-lg">
            {Math.abs(stats.mean) < 15
              ? "Votre placement est centré. C'est la signature d'un musicien de studio rigoureux."
              : stats.mean < 0
                ? "Vous pressez légèrement. Ce décalage donne de l'énergie (Punk/Rock) mais attention à ne pas perdre le contrôle."
                : "Vous jouez derrière le temps. C'est parfait pour le Blues/Jazz (Laid-back), tant que la grille reste stable."}
          </p>
        </div>
        <div className="text-right">
          <div className="text-[8px] font-black text-slate-500 uppercase tracking-widest mb-1">Threshold L{levelId}</div>
          <div className="text-2xl font-black text-purple-400 italic">±{Math.round(toleranceMs)}ms</div>
        </div>
      </div>
    </div>
  );
};
